import { useCallback, useEffect, useMemo, useState } from 'react';
import { toISODateString } from '@chainflip/utils/date';
import { isNotNullish } from '@chainflip/utils/guard';
import classNames from 'classnames';
import { ActiveFilters } from '@/shared/components/dropdown/filter/ActiveFilters';
import FilterDropdown from '@/shared/components/dropdown/filter/FilterDropdown';
import { ExportCsvButton } from '@/shared/components/ExportCSVButton';
import { SearchInput } from '@/shared/components/molecules/SearchInput';
import { PeriodSelector } from '@/shared/components/PeriodSelector';
import { LatestSwapsTable } from '@/shared/components/swaps/LatestSwapsTable';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { reportingClient } from '@/shared/graphql/client';
import {
  type GetAllFilteredSwapsQuery,
  type SwapRequestOverviewFragment,
} from '@/shared/graphql/generated/reporting/graphql';
import { getAllFilteredSwapsQuery, getAllSwapsQuery } from '@/shared/graphql/reporting/swaps';
import useDebouncedEffect from '@/shared/hooks/useDebouncedEffect';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import { triggerCsvDownload } from '@/shared/utils/export';
import { pipsToPercentString } from '@/shared/utils/numbers';
import {
  allSwapsFilters,
  type FilterOptionStore,
  isLabel,
  quickAction,
  selectedFilter,
  valueLabel,
} from 'packages/block-explorer/components/SwapsPage/AllSwapsFilter/FilterConfig';
import { extractSwapRequestId } from 'packages/block-explorer/helpers/allSwaps';
import { useAllSwapsFilterStore } from 'packages/block-explorer/hooks/useFilterStores';

export default function AllSwapsTable() {
  const [queryString, setQueryString] = useState('');
  const [debouncedQueryString, setDebouncedQueryString] = useState(queryString);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  useDebouncedEffect(
    () => setDebouncedQueryString(queryString),
    [queryString],
    queryString ? 1000 : 0,
  );

  const {
    assetsFrom,
    assetsTo,
    brokers,
    startDate,
    endDate,
    swapStates,
    swapTypes,
    refundAddress,
    destinationAddress,
    setFilter,
    swapSizeRange,
    resetAllFilters,
    getFilter,
    resetFilter,
  } = useAllSwapsFilterStore();

  const hasActiveFilters = useAllSwapsFilterStore((s) => s.hasActiveFilters());

  const [selectedDates, setSelectedDates] = useState([startDate, endDate]);

  useEffect(() => {
    if (
      startDate.getTime() !== selectedDates[0].getTime() ||
      endDate.getTime() !== selectedDates[1].getTime()
    )
      setSelectedDates([startDate, endDate]); // keep selected dates in sync with dates from the store
  }, [startDate, endDate]);

  const swapRequestId = extractSwapRequestId(debouncedQueryString.trim());

  const filter = useMemo(
    () => ({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      status: swapStates.length > 0 ? swapStates : undefined,
      assetsFrom: assetsFrom.length > 0 ? assetsFrom : undefined,
      brokers: brokers.length > 0 ? brokers : undefined,
      assetsTo: assetsTo.length > 0 ? assetsTo : undefined,
      isBoosted: swapTypes.includes('isBoosted') || undefined,
      isDca: swapTypes.includes('isDca') || undefined,
      isCcm: swapTypes.includes('isCcm') || undefined,
      isOnChain: swapTypes.includes('isOnChain') || undefined,
      isLiquidation: swapTypes.includes('isLiquidation') || undefined,
      isVaultSwap: swapTypes.includes('isVaultSwap') || undefined,
      refundAddress: refundAddress || undefined,
      destinationAddress: destinationAddress || undefined,
      minValueUsd:
        swapSizeRange[0] === 0 && swapSizeRange[1] === 0 ? undefined : swapSizeRange[0].toString(),
      maxValueUsd:
        swapSizeRange[0] === 0 && swapSizeRange[1] === 0 ? undefined : swapSizeRange[1].toString(),
    }),
    [
      assetsFrom,
      brokers,
      assetsTo,
      startDate,
      endDate,
      swapStates,
      swapTypes,
      refundAddress,
      destinationAddress,
      swapSizeRange,
    ],
  );

  const {
    data: swapRequestsByRequestId,
    isLoading: swapRequestsByIdLoading,
    paginationControls: swapRequestsByIdPaginationControls,
  } = useRemotePagination(getAllSwapsQuery, {
    useQueryParam: false,
    paginationLimits: [30, 60, 80, 100],
    initialLimit: 30,
    refetchInterval: TABLE_POLL_INTERVAL,
    variables: {
      swapRequestNativeId: swapRequestId,
    },
    context: {
      clientName: 'reportingService',
    },
    enabled: Boolean(swapRequestId),
  });

  const {
    data: swapRequestsByFilter,
    isLoading: swapsRequestsByFilterLoading,
    paginationControls: swapRequestsByFilterPaginationControls,
  } = useRemotePagination(getAllFilteredSwapsQuery, {
    useQueryParam: false,
    paginationLimits: [30, 60, 80, 100],
    initialLimit: 30,
    refetchInterval: TABLE_POLL_INTERVAL,
    variables: filter,
    context: {
      clientName: 'reportingService',
    },
    enabled: Boolean(!swapRequestId),
  });

  const exportCsv = useCallback(async () => {
    const BATCH_SIZE = 2_000;
    const MAX_SIZE = 10_000;
    let allSwaps: SwapRequestOverviewFragment[] = [];
    let afterCursor: string | null = null;
    let hasNextPage = true;

    while (hasNextPage && allSwaps.length < MAX_SIZE) {
      // eslint-disable-next-line no-await-in-loop
      const data: GetAllFilteredSwapsQuery = await reportingClient.request({
        document: getAllFilteredSwapsQuery,
        variables: {
          ...filter,
          first: BATCH_SIZE,
          after: afterCursor,
        },
      });

      const connection = data.allSwapRequests;
      allSwaps = allSwaps.concat(connection?.edges.map((edge) => edge.node) ?? []);
      hasNextPage = connection?.pageInfo.hasNextPage ?? false;
      afterCursor = connection?.pageInfo.endCursor ?? null;
    }

    const exportData = allSwaps.map((node) => {
      const oraclePriceDeltaPercent = isNotNullish(node.oraclePriceDeltaBps)
        ? pipsToPercentString(Number(node.oraclePriceDeltaBps))
        : null;

      const oraclePriceDeltaExFeesPercent = isNotNullish(node.oraclePriceDeltaExFeesBps)
        ? pipsToPercentString(Number(node.oraclePriceDeltaExFeesBps))
        : null;

      return [
        node.swapRequestNativeId,
        node.status,
        node.broker?.alias ?? node.mainBrokerAccountSs58Id ?? 'Internal',
        node.sourceAsset,
        node.destAsset,
        node.ingressAmount,
        node.ingressValueUsd,
        node.egressAmount,
        node.egressValueUsd,
        oraclePriceDeltaExFeesPercent ?? oraclePriceDeltaPercent,
        node.completedBlockDate,
      ];
    });

    triggerCsvDownload(`${toISODateString(startDate)}-${toISODateString(endDate)}-swaps-data.csv`, [
      [
        'Swap ID',
        'Status',
        'Broker',
        'Source Asset',
        'Dest Asset',
        'Input Amount',
        'Input Value USD',
        'Output Amount',
        'Output Value USD',
        'Price Delta',
        'Date',
      ],
      ...(exportData ?? []),
    ]);
  }, [filter]);

  const swapRequests = swapRequestsByRequestId ?? swapRequestsByFilter;
  const isLoading = swapRequestsByIdLoading || swapsRequestsByFilterLoading;
  const paginationControls = swapRequestId
    ? swapRequestsByIdPaginationControls
    : swapRequestsByFilterPaginationControls;

  return (
    <div className="flex w-full flex-col gap-y-8">
      <div className="flex items-center justify-between">
        <div className="text-left font-aeonikMedium text-24 text-cf-white">All Swaps</div>
        <div className="order-1 ml-auto truncate sm:w-96 lg:order-none lg:w-[32rem]">
          <SearchInput
            placeholder="Search by swap id..."
            value={queryString}
            onChange={setQueryString}
            className="border-cf-gray-5 bg-cf-gray-4 text-cf-light-2"
            placeholderClassName="text-cf-light-2"
          />
        </div>
      </div>
      <div>
        <div
          className={classNames(
            `flex flex-col gap-y-4 lg:flex-row lg:items-center`,
            'justify-between rounded-md rounded-b-none border border-b-0 border-cf-gray-4 bg-cf-gray-3 p-4',
          )}
        >
          <div className="flex w-full items-center">
            <div className="flex flex-wrap gap-2">
              <FilterDropdown<FilterOptionStore>
                isOpen={isFilterDropdownOpen}
                setIsOpen={setIsFilterDropdownOpen}
                resetAllFilters={resetAllFilters}
                hasActiveFilters={hasActiveFilters}
                allFilters={allSwapsFilters}
                selectedFilter={selectedFilter}
                quickAction={quickAction}
              />
              {Boolean(!swapRequestId) && (
                <ActiveFilters<FilterOptionStore>
                  getFilter={getFilter}
                  resetFilter={resetFilter}
                  allFilters={allSwapsFilters}
                  isLabel={isLabel}
                  valueLabel={valueLabel}
                  selectedFilter={selectedFilter}
                />
              )}
            </div>
            <div className="ml-auto flex items-center gap-x-4">
              <div className="self-stretch border-cf-gray-4 text-[8px] md:border-l" />
              <div className="min-w-[130px]">
                <PeriodSelector
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={(date) => setFilter('startDate', date)}
                  setEndDate={(date) => setFilter('endDate', date)}
                  selectedDates={selectedDates}
                  setSelectedDates={setSelectedDates}
                  buttonSize="small"
                />
              </div>
              <ExportCsvButton onExport={exportCsv} canExport={Boolean(swapRequests)} />
            </div>
          </div>
        </div>
        <LatestSwapsTable
          swapRequests={swapRequests}
          columns={['Swap Details', 'Addresses', 'Source', 'Status', 'Age']}
          isLoading={isLoading}
          paginationControls={paginationControls}
          preview={false}
          app="explorer"
          // showOracleDelta
        />
      </div>
    </div>
  );
}
